import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CheckTable from './CheckTable';
import { checkColumns, checkColumnsSingle } from './tableColumns';
import Excel from '../../../assets/excel.png';
import PDF from '../../../assets/pdf.png';
import { Button, Breadcrumbs, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import {
  generateReport,
  generateSingleReport,
  getReportData,
  removeReportData,
  getCheckImage,
  getList,
  userHasAccess,
} from '../operations';
import CheckPDFForm from './CheckPDFForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const CheckPdf = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSingle, setIsSingle] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [driverStatuses, setDriverStatuses] = useState([]);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  useEffect(() => {
    getList('driver-status').then(res => {
      const statuses = res.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      }));
      setDriverStatuses(statuses);
    });
  }, []);

  const fileName = `edge-reporting-check-${newDateFormatted.toString()}.csv`;

  const handleViewResults = async () => {
    setLoadingResults(true);
    const {
      FromPerEnddate = '',
      ToPerEnddate = '',
      EmpStatus = '',
      SSNO = '',
      EmplLastName = '',
      EmplFirstName = '',
      EmpId,
      checkId,
    } = form;
    try {
      if (isSingle) {
        const response = await generateReport({
          endpoint: 'check-driver-report',
          payload: {
            FromPerEnddate,
            ToPerEnddate,
            EmpStatus,
            SSNO,
            EmplLastName,
            EmplFirstName,
            EmpId,
          },
        });
        if (response.location && response.location !== '') {
          const reportData = await getReportData(response.location);
          if (reportData?.length) {
            await removeReportData(response.location);
            setData(reportData);
            setShowResults(true);
          }
        } else {
          alert('No data found matching your criteria.');
        }
      } else {
        const response = await generateSingleReport({
          endpoint: `check-report?checkId=${checkId}`,
        });
        if (response?.length) {
          setData(response);
          setShowResults(true);
        } else {
          alert('No data found matching your criteria.');
        }
      }
    } catch (error) {
      alert('No data found matching your criteria.');
    }
    setLoadingResults(false);
  };

  const handleExportPDF = async () => {
    setLoadingResults(true);
    const id = isSingle ? selectedCheck?.Check_ID : data[0].Check_ID;
    if (isSingle && !selectedCheck) {
      alert('Please select a check');
      return;
    }
    const pdf = await getCheckImage(id);
    setLoadingResults(false);
    if (pdf.includes('404')) {
      alert('PDF not available for this check');
      return;
    }
    const pdfWindow = window.open('');
    pdfWindow.document.write(
      `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(pdf)}'></iframe>`,
    );
  };

  const setSelectedRow = check => {
    setSelectedCheck(check);
  };

  const csvData = useMemo(
    () =>
      data?.length
        ? getCSVData(data, Object.keys(data[0]).length < 16 ? checkColumnsSingle(t).slice(1) : checkColumns(t))
        : [],
    [data],
  );

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Check PDF'}
          midItemAction={() => setShowResults(false)}
          rightTitle={
            showResults
              ? `Viewing Report ${isSingle ? `(${t('edge_reporting.check_pdf.driver')})` : '(Check Detail)'}`
              : 'Check PDF'
          }
          route="/edge-reporting"
        />
        {data?.length && showResults ? (
          <span>
            <ImageColorChanger
              src={PDF}
              onClick={handleExportPDF}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />

            <CSVLink filename={fileName} data={csvData}>
              <ImageColorChanger
                src={Excel}
                style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                alt="export-to-excel"
              />
            </CSVLink>
          </span>
        ) : null}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <CheckTable data={data} loading={loadingResults} rowSelected={setSelectedRow} isSingle={isSingle} />
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <CheckPDFForm
              form={form}
              setForm={setForm}
              setIsSingle={setIsSingle}
              isSingle={isSingle}
              driverStatuses={driverStatuses}
              handleViewResults={handleViewResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default CheckPdf;
